import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { CalendarEventSearchResult } from './scheduleApi'

dayjs.extend(utc)

export const MINIMUM_SEARCH_TERM_LENGTH = 2
export const SEARCH_PAGE_SIZE = 25
export const SEARCH_DEBOUNCE_MS = 300

export const isSearchable = (searchTerm: string) => searchTerm.trim().length >= MINIMUM_SEARCH_TERM_LENGTH

export type SearchResultGroup = {
  key: string
  jobId: number | null
  jobName: string
  jobNumber: string | null
  customerDisplayName: string | null
  occurrences: CalendarEventSearchResult[]
}

export const groupResultsByJob = (results: CalendarEventSearchResult[]): SearchResultGroup[] => {
  const groups: SearchResultGroup[] = []
  const byKey = new Map<string, SearchResultGroup>()

  results.forEach(result => {
    const key = result.jobId !== null ? `job-${result.jobId}` : `event-${result.id}`
    let group = byKey.get(key)
    if (!group) {
      group = {
        key,
        jobId: result.jobId,
        jobName: result.jobName,
        jobNumber: result.jobNumber,
        customerDisplayName: result.customerDisplayName,
        occurrences: []
      }
      byKey.set(key, group)
      groups.push(group)
    }
    group.occurrences.push(result)
  })

  return groups
}

export const occurrenceDate = (occurrence: CalendarEventSearchResult): Dayjs => dayjs.utc(occurrence.startUtc).local()

export const formatOccurrence = (occurrence: CalendarEventSearchResult): string => {
  const start = occurrenceDate(occurrence)
  if (occurrence.allDay) {
    return start.format('ddd, MMM D, YYYY')
  }
  return start.format('ddd, MMM D, YYYY · h:mm A')
}

export const formatOccurrenceCount = (count: number): string => {
  return count === 1 ? '1 scheduled date' : `${count} scheduled dates`
}

export const buildResultSubtitle = (group: SearchResultGroup): string => {
  return [group.jobNumber, group.customerDisplayName].filter(Boolean).join(' · ')
}

export const MAX_VISIBLE_OCCURRENCES = 5

export const visibleOccurrences = (group: SearchResultGroup, isExpanded: boolean): CalendarEventSearchResult[] => {
  if (isExpanded) {
    return group.occurrences
  }
  return group.occurrences.slice(0, MAX_VISIBLE_OCCURRENCES)
}

export const hiddenOccurrenceCount = (group: SearchResultGroup): number => {
  return Math.max(0, group.occurrences.length - MAX_VISIBLE_OCCURRENCES)
}

export type HighlightableEvent = {
  jobName?: string | null
  jobNumber?: string | null
  customerDisplayName?: string | null
  customer?: { displayName?: string | null } | null
}

export const eventMatchesSearchTerm = (event: HighlightableEvent, searchTerm: string): boolean => {
  const term = searchTerm.trim().toLowerCase()
  if (!isSearchable(term)) {
    return true
  }

  const customerName = event.customerDisplayName ?? event.customer?.displayName ?? null
  return [event.jobName, event.jobNumber, customerName]
    .some(field => (field ?? '').toLowerCase().includes(term))
}

export const DIMMED_OPACITY = 0.25

export const searchDimmingOpacity = (event: HighlightableEvent, searchTerm: string): number => {
  return eventMatchesSearchTerm(event, searchTerm) ? 1 : DIMMED_OPACITY
}

export const SEARCH_COLLAPSE_MAX_WIDTH = 1900

export const isCollapsibleWidth = (viewportWidth: number) => viewportWidth <= SEARCH_COLLAPSE_MAX_WIDTH

export const shouldShowSearchInput = (viewportWidth: number, isExpanded: boolean) =>
  !isCollapsibleWidth(viewportWidth) || isExpanded

export const shouldCollapseOnBlur = (viewportWidth: number, isResultsPanelOpen: boolean, searchTerm: string) =>
  isCollapsibleWidth(viewportWidth) && !isResultsPanelOpen && searchTerm.trim().length === 0
